import React, { Suspense } from "react";
import get from "lodash/get";
import set from "lodash/set";
import mapValues from "lodash/mapValues";
import cloneDeep from "lodash/cloneDeep";
import { RiArrowGoBackFill } from "react-icons/ri";
import { Form, Label, Button, Input } from "reactstrap";
import { DateInput } from "components/sites/DateInput";
import { useTranslation } from "react-i18next";
import { SelectStep } from "../common/Select/SelectStep";
import { SelectUnit } from "../common/Select/SelectUnit";
import { SelectTypeConso } from "./SelectTypeConso";
import { SelectConsoForm } from "./SelectConsoForm";
import { useReferentiel } from "./useReferentiel";
import { SelectHorosaisonnier } from "./SelectHorosaisonnier";
import { SelectHorosaisonnierStep } from "./SelectHorosaisonnierStep";
import {
  downloadTemplateAction,
  importConsumptionAction,
} from "actions/dataManagement";
import { useDispatch, useSelector } from "react-redux";
import Notifications from "react-notification-system-redux";

const notifySuccess = (t) =>
  Notifications.success({
    title: t("dataManagement.notify.importSuccess"),
    position: "bl",
    autoDismiss: 3,
  });
const notifyError = (t) =>
  Notifications.error({
    title: t("dataManagement.notify.error"),
    position: "bl",
    autoDismiss: 3,
  });

export const Import = ({ onBack, selectedIds }) => {
  const [data, setData] = React.useState({});
  const [touched, setTouched] = React.useState(false);
  const { ListeHorosaisonnier, ListDesUnites, isLoading } = useReferentiel();
  const [selectedFile, setSelectedFile] = React.useState(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = false;

  const rawReferentiel = { ListeHorosaisonnier, ListDesUnites };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const formattedOptions = React.useMemo(() => {
    return mapValues(rawReferentiel, (list) => {
      if (!Array.isArray(list)) return [];

      return list.map((item) => ({
        value: item.Id,
        label: item.Nom,
      }));
    });
  }, [ListeHorosaisonnier]);

  const {
    ListeHorosaisonnier: horoOptions,
    ListDesUnites: unitOptions,
  } = formattedOptions;

  const onChange = React.useCallback(
    (path, value) => {
      setTouched(true);
      const newData = cloneDeep(data);
      set(newData, path, value);
      setData(newData);
    },
    [data],
  );

  const importConsumption = React.useCallback(
    async (e) => {
      e.preventDefault();

      const jsonData = {
        PdlNums: selectedIds,
        Start: data.DateEntree,
        End: data.DateSortie,
        Timestep: data.Pas,
        TypeConso: data.TypeConso,
        FormConso: data.FormConso,
        Horosaisonnier: data.Horosaisonnier,
        Unit: data.Unit,
      };

      const formData = new FormData();

      formData.append("consommation", JSON.stringify(jsonData));
      formData.append("csvfile", selectedFile);

      try {
        await dispatch(importConsumptionAction(formData));

        dispatch(notifySuccess(t));
        setTouched(false);
      } catch (error) {
        console.error("Import failed:", error);
        dispatch(notifyError(t));
      }
    },
    [data, selectedIds, selectedFile, dispatch],
  );

  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault();

      const payload = {
        PdlNums: selectedIds,
        Start: data.DateEntree,
        End: data.DateSortie,
        Timestep: data.Pas,
        TypeConso: data.TypeConso,
        FormConso: data.FormConso,
        Horosaisonnier: data.Horosaisonnier,
        Unit: data.Unit,
      };

      try {
        await dispatch(downloadTemplateAction(payload));
      } catch (error) {
        console.error("Download template failed:", error);
        dispatch(notifyError(t));
      }
    },
    [data, selectedIds, dispatch],
  );

  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        alignItems: "flex-start",
      }}
    >
      <Button
        style={{ color: "black", background: "#98D2DD", border: "#98D2DD" }}
        onClick={onBack}
      >
        <RiArrowGoBackFill />
        {t("dataManagement.back")}
      </Button>
      <div>
        <Form onSubmit={handleSubmit}>
          <div>
            <Label>
              <span className="mr-2 font-weight-bold">
                {t("dataManagement.startDate")}
              </span>
              <DateInput
                value={get(data, "DateEntree", "")}
                onChange={(value) => onChange("DateEntree", value)}
                disabled={loading}
              />
            </Label>
          </div>

          <div>
            <Label>
              <span className="mr-2 font-weight-bold">
                {t("dataManagement.endDate")}
              </span>
              <DateInput
                value={get(data, "DateSortie", "")}
                onChange={(value) => onChange("DateSortie", value)}
                disabled={loading}
              />
            </Label>
          </div>
          <div>
            <Label>
              <SelectTypeConso
                value={get(data, "TypeConso", "")}
                onChange={(value) => onChange("TypeConso", value)}
              />
            </Label>
          </div>
          <div>
            <Label>
              <SelectUnit
                options={unitOptions}
                disabled={isLoading}
                value={get(data, "Unit", "")}
                onChange={(value) => onChange("Unit", value)}
              />
            </Label>
          </div>
          <div>
            <Label>
              <SelectConsoForm
                value={get(data, "FormConso", "")}
                onChange={(value) => onChange("FormConso", value)}
              />
            </Label>
          </div>
          {get(data, "FormConso", "") === 2 && (
            <div>
              <Label>
                <SelectHorosaisonnier
                  options={horoOptions}
                  disabled={isLoading}
                  value={get(data, "Horosaisonnier", "")}
                  onChange={(value) => onChange("Horosaisonnier", value)}
                />
              </Label>
            </div>
          )}
          {get(data, "FormConso", "") === 2 && (
            <div>
              <Label>
                <SelectHorosaisonnierStep
                  value={get(data, "Pas", "")}
                  onChange={(value) => onChange("Pas", value)}
                />
              </Label>
            </div>
          )}
          {get(data, "FormConso", "") !== 2 && (
            <div>
              <Label>
                <SelectStep
                  value={get(data, "Pas", "")}
                  onChange={(value) => onChange("Pas", value)}
                />
              </Label>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !touched}
            style={{ color: "black", background: "#98D2DD", border: "#98D2DD" }}
          >
            {t("dataManagement.downloadTemplate")}
          </Button>
        </Form>
      </div>
      <div>
        <Form onSubmit={importConsumption}>
          <div>
            <Label>
              <span className="mr-2 font-weight-bold">
                {t("dataManagement.file")}
              </span>
              <Input type="file" onChange={handleFileChange} />
            </Label>
          </div>
          <div>
            <Button
              type="submit"
              disabled={loading || !touched}
              style={{
                color: "black",
                background: "#98D2DD",
                border: "#98D2DD",
                marginTop: "1rem",
              }}
            >
              {t("dataManagement.import")}
            </Button>
          </div>
        </Form>
      </div>
    </section>
  );
};
